/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import type { ReactNode } from "react";
import type { CellProps } from "../../components/cell/cell.component";
import { calculateMaxColumnWidth } from "../calculate-max-column-width/calculate-max-column-width.util";
import { calculateOptimalColumnWidth } from "../calculate-optimal-column-width/calculate-optimal-column-width.util";

/**
 * Column definition for tableOptions API
 */
interface TableOptionsColumn<TData> {
  /** Unique column identifier (used for storage) */
  id: string;
  /** Plain text label (used in customize dialog and accessibility) */
  label: string;
  /** Render function for the column header (receives label, returns JSX) */
  children: (label: string) => ReactNode;
  /** Getter function to extract data value for width calculations */
  getter: (row: TData) => string | number | boolean | null | undefined;
  /** Minimum column width in pixels (set based on header size) */
  minWidth?: number;
  /** Whether the column can be resized */
  allowsResizing?: boolean;
  /** Whether the column can be sorted */
  allowsSorting?: boolean;
  /** Whether this column is the row header */
  isRowHeader?: boolean;
  /** Cell render function that returns cell configuration */
  cell: (row: TData) => CellProps;
}

/**
 * Table column configuration (for visibility/ordering)
 */
export interface TableColumnConfig {
  id: string;
  label: string;
  visible: boolean;
}

export interface SkeletonRow {
  __skeleton: true;
  id: string;
}

/**
 * Bound table configuration with pre-calculated widths
 */
interface BoundTableConfig<TData> {
  /** Ready-to-use layout options for virtualization */
  layoutOptions: {
    columnWidths: Map<string, number>;
    rowHeight: number;
    headingHeight: number;
    gap: number;
  };
  /** Get columns with calculated widths */
  getColumns: (options?: { hasEmptySpacerColumn?: boolean }) => {
    id: string;
    label: ReactNode;
    children: ReactNode;
    textValue: string;
    defaultWidth: number;
    maxContentWidth: number;
    minWidth: number;
    allowsResizing?: boolean;
    allowsSorting?: boolean;
    isFirst?: boolean;
    isLast?: boolean;
    isRowHeader?: boolean;
    isPadded?: boolean;
  }[];
  /** Get cell configuration for a specific column and row */
  getCell: (columnId: string, row: TData) => CellProps | undefined;
  /** Get skeleton rows for loading state */
  getSkeletonRows: (count?: number) => SkeletonRow[];
  /** Type guard for skeleton rows */
  isSkeletonRow: (row: TData | SkeletonRow) => row is SkeletonRow;
}

/**
 * Table factory function with initial config
 */
interface TableOptionsFactory<TData> {
  /** Bind data to create table configuration */
  (data: TData[], columnsConfig: TableColumnConfig[]): BoundTableConfig<TData>;
  /** Initial configuration for local storage */
  initialConfig: TableColumnConfig[];
}

/**
 * Two-phase table configuration helper
 *
 * Phase 1: Define table structure with column definitions
 * Phase 2: Bind data to calculate widths and create ready-to-use config
 *
 * @param columns - Array of column definitions
 * @returns Factory function that binds data to create table configuration
 *
 * @example
 * ```tsx
 * // Phase 1: Define structure
 * const TABLE = tableOptions<CampaignRow>([
 *   {
 *     id: 'id',
 *     label: 'ID',
 *     children: (label) => <Text>{label}</Text>,
 *     getter: (row) => row.id,
 *     minWidth: 40,
 *     allowsResizing: true,
 *     isRowHeader: true,
 *     cell: (row) => ({
 *       children: <Text>{row.id}</Text>,
 *       href: `/campaigns/${row.id}`,
 *       isInternal: true
 *     })
 *   }
 * ]);
 *
 * // Phase 2: Bind data in component
 * const config = useMemo(
 *   () => TABLE(rows, columnsConfig),
 *   [rows, columnsConfig]
 * );
 * ```
 */
export const tableOptions = <TData>(columns: TableOptionsColumn<TData>[]): TableOptionsFactory<TData> => {
  // Create initial config for storage (using string labels)
  const initialConfig: TableColumnConfig[] = columns.map((colDef) => ({
    id: colDef.id,
    label: colDef.label,
    visible: true,
  }));

  // Create the factory function that binds data
  const factory = (
    data: TData[],
    columnsConfig: TableColumnConfig[],
  ): BoundTableConfig<TData> => {
    const hasData = data.length > 0;

    // Create a map of column definitions by their ID
    const columnDefsById = new Map<string, TableOptionsColumn<TData>>();
    columns.forEach((colDef) => {
      columnDefsById.set(colDef.id, colDef);
    });

    // Normalize columnsConfig: preserve user order, then append any new columns
    // that only exist in definitions (e.g. newly added columns).
    const columnDefIds = new Set(columns.map((colDef) => colDef.id));

    const normalizedColumnsConfig: TableColumnConfig[] = [
      // User-ordered columns (filter out any that no longer exist in definitions)
      ...columnsConfig.filter((colConfig) => columnDefIds.has(colConfig.id)),
      // Append new columns that aren't in the user config yet
      ...columns
        .filter((colDef) => !columnsConfig.some((c) => c.id === colDef.id))
        .map((colDef) => ({ id: colDef.id, label: colDef.label, visible: true })),
    ];

    // Calculate widths for all columns
    const columnWidths = new Map<string, {
      defaultWidth: number;
      maxContentWidth: number;
      minWidth: number;
    }>();

    columns.forEach((colDef) => {
      const columnId = colDef.id;

      // Get text extractor that uses the getter function
      const textExtractor = (row: TData): string => {
        const value = colDef.getter(row);
        return value != null ? String(value) : '';
      };

      const userMinWidth = colDef.minWidth ?? 40;

      // Calculate widths only if we have data
      const calculatedOptimalWidth = calculateOptimalColumnWidth(data, textExtractor, {
        minAcceptable: 40,
        outlierThreshold: 0.005, // Trim if >0.5% of values are outliers (instead of default 10%)
      });

      const calculatedMaxWidth = hasData
        ? calculateMaxColumnWidth(data, textExtractor, { minWidth: 40 })
        : userMinWidth + 50; // Use minWidth + buffer as default when no data

      // Apply minWidth only to defaultWidth (for layout purposes)
      const defaultWidth = Math.max(calculatedOptimalWidth, userMinWidth);

      columnWidths.set(columnId, {
        defaultWidth,
        maxContentWidth: calculatedMaxWidth,
        minWidth: userMinWidth,
      });
    });

    // Filter visible columns and apply ordering from columnsConfig
    const visibleColumns = normalizedColumnsConfig.filter(({ visible }) => visible);

    // Create layoutColumnWidths for virtualization
    const layoutColumnWidths = new Map<string, number>();

    visibleColumns.forEach((colConfig) => {
      const widthData = columnWidths.get(colConfig.id);
      layoutColumnWidths.set(colConfig.id, widthData?.defaultWidth ?? 150);
    });

    // Determine if the last column's alignment would need an empty spacer
    let spacerNeeded = false;
    const lastColumn = visibleColumns[visibleColumns.length - 1];
    if (lastColumn) {
      const colDef = columnDefsById.get(lastColumn.id);
      spacerNeeded = colDef && hasData
        ? (() => {
          const cellConfig = colDef.cell(data[0]);
          return cellConfig.align !== 'left' && cellConfig.align !== 'padded';
        })()
        : true; // Default to true if no data
    }

    // Always include spacer width in layout (harmless if unused by columns)
    if (spacerNeeded) {
      layoutColumnWidths.set('empty-spacer', 48);
    }

    return {
      layoutOptions: {
        columnWidths: layoutColumnWidths,
        rowHeight: 36,
        headingHeight: 36,
        gap: 0,
      },

      getColumns: (options?: { hasEmptySpacerColumn?: boolean }) => {
        const { hasEmptySpacerColumn = true } = options ?? {};

        const columns = visibleColumns.map((colConfig, index) => {
          const colDef = columnDefsById.get(colConfig.id);
          const widths = columnWidths.get(colConfig.id);
          const renderedLabel = colDef ? colDef.children(colDef.label) : colConfig.label;
          const isFirstColumn = index === 0;
          const isLastColumn = index === visibleColumns.length - 1;

          return {
            id: colConfig.id,
            label: renderedLabel,
            children: renderedLabel,
            textValue: colConfig.label,
            defaultWidth: widths?.defaultWidth ?? 150,
            maxContentWidth: widths?.maxContentWidth ?? 200,
            minWidth: widths?.minWidth ?? 40,
            allowsResizing: colDef?.allowsResizing,
            allowsSorting: colDef?.allowsSorting,
            isFirst: isFirstColumn ? true : undefined,
            isLast: isLastColumn ? true : undefined,
            isRowHeader: colDef?.isRowHeader,
            isPadded: isFirstColumn ? true : undefined,
          };
        });

        // Add empty spacer if needed
        if (hasEmptySpacerColumn && spacerNeeded) {
          columns.push({
            id: 'empty-spacer',
            label: '',
            children: '',
            textValue: '', // Empty textValue for spacer
            // @ts-expect-error defaultWidth is '1fr' for spacer column
            defaultWidth: '1fr',
            maxContentWidth: 48,
            minWidth: 48,
            allowsResizing: false,
            allowsSorting: undefined,
            isFirst: undefined,
            isLast: undefined,
            isRowHeader: undefined,
            isPadded: undefined,
          });
        }

        return columns;
      },

      getCell: (columnId: string, row: TData) => {
        const colDef = columnDefsById.get(columnId);
        if (!colDef) {
          return undefined;
        }

        const cellConfig = colDef.cell(row);
        const isFirstColumn = visibleColumns.length > 0 && visibleColumns[0].id === columnId;
        const isLastColumn = visibleColumns.length > 0 && visibleColumns[visibleColumns.length - 1].id === columnId;

        // Auto-generate textValue from getter if not provided
        const textValue = cellConfig.textValue ?? String(colDef.getter(row) ?? '');

        return {
          ...cellConfig,
          // First column cells should always have align="padded"
          ...(isFirstColumn && { align: 'padded' as const }),
          // Last column cells should be borderless
          ...(isLastColumn && { isBorderless: true }),
          textValue,
        };
      },
      getSkeletonRows: (count = 50) =>
        Array.from({ length: count }, (_, index) => ({
          __skeleton: true,
          id: `skeleton-${index}`,
        })),
      isSkeletonRow: (row: TData | SkeletonRow): row is SkeletonRow =>
        typeof row === 'object' && row !== null && '__skeleton' in row,
    };
  };

  // Attach initialConfig as a property
  factory.initialConfig = initialConfig;

  return factory;
};
